import { hermitePoints, type Point } from './hermite';
import { displayZ } from './volume';
import type { Frame, Volume } from './types';

/** In-plane bilinear sample of the volume at (x,y) on integer slice z. HU. */
function sampleXY(vol: Volume, x: number, y: number, z: number): number {
    const { data, ny, nx } = vol;
    if (x < 0) x = 0;
    else if (x > nx - 1) x = nx - 1;
    if (y < 0) y = 0;
    else if (y > ny - 1) y = ny - 1;
    const x0 = x | 0,
        y0 = y | 0;
    const x1 = x0 + 1 < nx ? x0 + 1 : x0;
    const y1 = y0 + 1 < ny ? y0 + 1 : y0;
    const fx = x - x0,
        fy = y - y0;
    const base = z * ny * nx;
    const v00 = data[base + y0 * nx + x0];
    const v10 = data[base + y0 * nx + x1];
    const v01 = data[base + y1 * nx + x0];
    const v11 = data[base + y1 * nx + x1];
    return v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
}

/** Trace points (axial-frame fractions) → axial voxel coords. */
export function traceToVoxel(trace: Point[], vol: Volume): Point[] {
    return trace.map(([fx, fy]) => [fx * (vol.nx - 1), fy * (vol.ny - 1)] as Point);
}

/** Resample a polyline to ~stepVox-spaced points along its arc length. */
function arcResample(poly: Point[], stepVox: number): Point[] {
    if (poly.length < 2) return poly.slice();
    const cum = [0];
    for (let i = 1; i < poly.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(poly[i][0] - poly[i - 1][0], poly[i][1] - poly[i - 1][1]));
    }
    const total = cum[cum.length - 1];
    const n = Math.max(2, Math.round(total / stepVox));
    const out: Point[] = [];
    let seg = 1;
    for (let i = 0; i < n; i++) {
        const d = (i / (n - 1)) * total;
        while (seg < cum.length - 1 && cum[seg] < d) seg++;
        const t = (d - cum[seg - 1]) / Math.max(cum[seg] - cum[seg - 1], 1e-6);
        out.push([
            poly[seg - 1][0] + t * (poly[seg][0] - poly[seg - 1][0]),
            poly[seg - 1][1] + t * (poly[seg][1] - poly[seg - 1][1])
        ]);
    }
    return out;
}

/** Dense, arc-length-uniform arch (voxel coords) + per-column unit normals. */
export function archColumns(trace: Point[], vol: Volume): { pts: Point[]; normals: Point[] } {
    const dense = hermitePoints(traceToVoxel(trace, vol), 24);
    const pts = arcResample(dense, 1); // ~1 voxel per column
    const normals: Point[] = pts.map((_, i) => {
        const a = pts[Math.max(0, i - 1)];
        const b = pts[Math.min(pts.length - 1, i + 1)];
        const tx = b[0] - a[0],
            ty = b[1] - a[1];
        const len = Math.hypot(tx, ty) || 1;
        return [-ty / len, tx / len]; // perpendicular to tangent
    });
    return { pts, normals };
}

/** Optional vertical crop: render only rows [start, start+count) (row 0 = superior). */
export interface RowRange {
    start: number;
    count: number;
}

/**
 * Curved-MPR panorama: for each arch column and each depth z, mean-project over
 * a focal trough of `thicknessVox` perpendicular to the arch. Rows = depth,
 * top = superior. Output (W × nz) in HU.
 */
export function buildPanorama(
    vol: Volume,
    pts: Point[],
    normals: Point[],
    thicknessVox: number,
    rows?: RowRange,
    flipVertical = false
): Frame {
    const W = pts.length;
    const nz = vol.nz;
    const rStart = rows ? rows.start : 0;
    const rCount = rows ? rows.count : nz;
    const half = Math.max(0, thicknessVox / 2);
    const nOff = Math.max(1, Math.min(48, Math.round(thicknessVox) + 1));
    const offsets: number[] = [];
    for (let k = 0; k < nOff; k++)
        offsets.push(nOff === 1 ? 0 : -half + (2 * half * k) / (nOff - 1));

    const out = new Float32Array(W * rCount);
    for (let ri = 0; ri < rCount; ri++) {
        const z = displayZ(nz, rStart + ri, flipVertical);
        const rowBase = ri * W;
        for (let c = 0; c < W; c++) {
            const [ax, ay] = pts[c];
            const [nxv, nyv] = normals[c];
            let sum = 0;
            for (let o = 0; o < offsets.length; o++) {
                sum += sampleXY(vol, ax + nxv * offsets[o], ay + nyv * offsets[o], z);
            }
            out[rowBase + c] = sum / offsets.length;
        }
    }
    return { data: out, w: W, h: rCount };
}

/**
 * One perpendicular cross-section at arch position `col`: cut along the arch
 * normal (buccolingual), spanning ±halfWidthVox, × depth z. Output (Wcut × nz).
 */
export function buildCrossSection(
    vol: Volume,
    pts: Point[],
    normals: Point[],
    col: number,
    halfWidthVox: number,
    flip = false, // mirror the buccolingual (A↔P) axis — used for the right TMJ so both sides read alike
    rows?: RowRange,
    flipVertical = false // reverse the superior/inferior (z) orientation — see `displayZ`
): Frame {
    const nz = vol.nz;
    const rStart = rows ? rows.start : 0;
    const rCount = rows ? rows.count : nz;
    const Wcut = Math.max(2, Math.round(2 * halfWidthVox));
    const [ax, ay] = pts[col];
    const [nxv, nyv] = normals[col];
    const s = flip ? -1 : 1;
    const out = new Float32Array(Wcut * rCount);
    for (let ri = 0; ri < rCount; ri++) {
        const z = displayZ(nz, rStart + ri, flipVertical);
        const rowBase = ri * Wcut;
        for (let u = 0; u < Wcut; u++) {
            const off = s * (-halfWidthVox + (2 * halfWidthVox * u) / (Wcut - 1));
            out[rowBase + u] = sampleXY(vol, ax + nxv * off, ay + nyv * off, z);
        }
    }
    return { data: out, w: Wcut, h: rCount };
}

export type CephView = 'lateral' | 'frontal';

// Air is -1000 HU. Offsetting by +1000 puts air at 0, so `max(0, HU + 1000)` is
// a non-negative "density" per voxel (air contributes nothing, soft tissue ~1000,
// bone ~2000+). Used by the summed cephalogram below.
const AIR_OFFSET = 1000;
// Bone-emphasis ramp: HU below EMPH_LO gets no boost (soft tissue), fully boosted
// at/above EMPH_HI (mineralised bone). A `boost` of 0 leaves the plain density sum.
const EMPH_LO = 150,
    EMPH_HI = 1500;

// Per-HU contribution table for buildCephSum. The volume is Int16, so a table
// spanning the whole Int16 domain is indexed by `hu + 32768` with no clamp/branch;
// the d>0 test and bone-emphasis ramp are baked in, turning the inner projection
// loop into a single array read (no function call, no branch, no offset add).
// Cached per `boost` since it depends only on boost (+ the constants above).
const CEPH_LUT_OFFSET = 32768; // -(Int16 min)
const CEPH_LUT_SIZE = 65536; // Int16 range
// Quantize the boost so tiny slider-driven float differences don't each mint a
// distinct 256 KB LUT, and cap the cache so it can't grow unbounded for the app
// lifetime (oldest-first eviction).
const CEPH_LUT_CACHE_MAX = 8;
const cephLutCache = new Map<number, Float32Array>();
const quantizeBoost = (boost: number): number => Math.round(boost * 100) / 100;

function cephLut(boostRaw: number): Float32Array {
    const boost = quantizeBoost(boostRaw);
    const cached = cephLutCache.get(boost);
    if (cached) {
        // Refresh recency so the freshly-used entry survives eviction.
        cephLutCache.delete(boost);
        cephLutCache.set(boost, cached);
        return cached;
    }
    const lut = new Float32Array(CEPH_LUT_SIZE);
    for (let i = 0; i < CEPH_LUT_SIZE; i++) {
        const hu = i - CEPH_LUT_OFFSET;
        const d = hu + AIR_OFFSET;
        if (d <= 0) continue; // air / below-air contributes nothing (LUT pre-zeroed)
        if (boost === 0 || hu <= EMPH_LO) {
            lut[i] = d;
            continue;
        }
        const f = hu >= EMPH_HI ? 1 : (hu - EMPH_LO) / (EMPH_HI - EMPH_LO);
        lut[i] = d * (1 + boost * f);
    }
    if (cephLutCache.size >= CEPH_LUT_CACHE_MAX) {
        const oldest = cephLutCache.keys().next().value;
        if (oldest !== undefined) cephLutCache.delete(oldest);
    }
    cephLutCache.set(boost, lut);
    return lut;
}

/**
 * Synthetic cephalogram: a full-depth ray projection off the volume (not a
 * single slice). Offset every voxel by +1000 so air sits at 0, and sum
 * `max(0, HU+1000) · (1 + boost·boneFraction)` along the projection axis.
 * `boost = 0` is the plain density sum (foggy — bone barely out-sums the
 * soft-tissue mass); raising `boost` multiplies bone voxels up before summing,
 * so skin fades and the skeleton stands out. The panel windows it for display.
 *
 *  - lateral: sum along the L-R axis (x) → a sagittal-plane image (ny × nz),
 *    P left / A right, superior up. Matches `planes.sagittal`.
 *  - frontal (PA): sum along the A-P axis (y) → a coronal-plane image
 *    (nx × nz), R left / L right, superior up. Matches `planes.coronal`.
 */
export function buildCephSum(vol: Volume, view: CephView, boost = 0, flipVertical = false): Frame {
    const { data, nz, ny, nx } = vol;
    const lut = cephLut(boost);
    const OFF = CEPH_LUT_OFFSET;
    if (view === 'lateral') {
        const out = new Float32Array(ny * nz);
        for (let r = 0; r < nz; r++) {
            const z = displayZ(nz, r, flipVertical);
            const dst = r * ny;
            for (let y = 0; y < ny; y++) {
                const rowBase = z * ny * nx + y * nx;
                let sum = 0;
                for (let x = 0; x < nx; x++) sum += lut[data[rowBase + x] + OFF];
                out[dst + (ny - 1 - y)] = sum; // posterior left / anterior right
            }
        }
        return { data: out, w: ny, h: nz };
    }
    // frontal (PA)
    const out = new Float32Array(nx * nz);
    for (let r = 0; r < nz; r++) {
        const z = displayZ(nz, r, flipVertical);
        const dst = r * nx;
        for (let x = 0; x < nx; x++) {
            const base = z * ny * nx + x;
            let sum = 0;
            for (let y = 0; y < ny; y++) sum += lut[data[base + y * nx] + OFF];
            out[dst + x] = sum;
        }
    }
    return { data: out, w: nx, h: nz };
}
